import React from 'react'
import { ViewStyle } from 'react-native'
import { useSelector } from 'react-redux'
import { StyleProp, View } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { useNavigation } from '@react-navigation/native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { TouchableSection } from '@src/components'
import { useIsDarkMode } from '@src/config/theme'
import { uiFlagsSelector } from '@src/selectors/uiFlags'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { useOnboardingExperienceBanner } from '@src/screens/Home/components/WeightLossExperience/useBanner'
import { userSelector } from '@src/selectors/app'
import { ContentLoader, Rect } from '@src/components/ContentLoader'
import StressManagementIcon from '@src/assets/svgs/stressManagement'
import { useStressManagementOffering } from './useStressManagementOffering'

interface StressManagementBannerProps {
  skipLoadingSchedulingInfo?: boolean
  style?: StyleProp<ViewStyle>
}

export const StressManagementBanner = ({
  skipLoadingSchedulingInfo = false,
  style,
}: StressManagementBannerProps) => {
  const styles = useStyleSheet(themedStyles)
  const isDarkMode = useIsDarkMode()
  const uiFlags = useSelector(uiFlagsSelector)
  const navigation = useNavigation()
  const theme = useTheme()

  const { bannerProps, loading, error } = useStressManagementOffering({ skipLoadingSchedulingInfo })

  // TODO: Move all of this logic to the backend, esp. the weight-loss banner showing logic
  const user = useSelector(userSelector)
  const { shouldShowBanner: showingWeightLossBanner } = useOnboardingExperienceBanner()
  const showPopup =
    uiFlags.showStressManagementBanner && !showingWeightLossBanner && !user?.uhcMember && !error

  useLazyScreenPreload('StressManagementIntroScreen', { shouldPreload: showPopup })

  if (!showPopup) {
    return null
  }

  const onPress = () => {
    if (loading) {
      return
    }
    navigation.navigate('StressManagementIntroScreen', { source: 'banner' })
  }

  const loaderColors = isDarkMode
    ? {
        backgroundColor: theme['theme.surface.base1'],
        foregroundColor: theme['theme.surface.base'],
      }
    : {
        backgroundColor: theme['theme.marketing.lighterYellow'],
        foregroundColor: theme['theme.marketing.lightYellow'],
      }

  const iconColor = isDarkMode
    ? theme['theme.tooltip.textPrimary']
    : theme['theme.marketing.subtleText']

  const textColor = isDarkMode ? theme['theme.tooltip.textPrimary'] : theme['theme.text.primary']
  const backgroundColor = isDarkMode
    ? theme['theme.marketing.yellow']
    : theme['theme.marketing.lightYellow']
  const rightArrowColor = isDarkMode ? theme['theme.solid.black'] : undefined

  return (
    <View style={style}>
      <TouchableSection
        {...bannerProps}
        onPress={onPress}
        style={[styles.content, { backgroundColor }]}
        subtitleStyle={[styles.subtitle, { color: textColor }]}
        labelStyle={{ color: textColor }}
        accessoryLeft={
          <View style={styles.iconContainer}>
            <StressManagementIcon
              size={40}
              strokeColor={iconColor}
              strokeWidth={4}
              fillColor1="none"
              fillColor2="none"
            />
          </View>
        }
        showRightArrow={!loading}
        rightArrowColor={rightArrowColor}
      />
      {loading && (
        <ContentLoader style={styles.loader} {...loaderColors}>
          <Rect x="88" y="16" width="55%" height="20" rx="6" ry="6" />
          <Rect x="88" y="48" width="70%" height="16" rx="4" ry="4" />
        </ContentLoader>
      )}
    </View>
  )
}

const themedStyles = StyleService.create({
  subtitle: {
    maxWidth: 246,
  },
  content: {
    height: 'auto',
    borderRadius: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
    height: 56,
    width: 56,
  },
  loader: {
    position: 'absolute',
    height: '100%',
    width: '100%',
  },
})
